import { Container } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W } from '../app/theme';
import { Button, makeText, sceneBackground, avatarSprite, iconSprite, glassCard } from '../ui/widgets';
import { OptionPicker } from '../ui/OptionPicker';
import { Slider } from '../ui/Slider';
import { TextField } from '../ui/TextField';
import { enterFade, pageHeader } from './common';
import { viewport } from '../app/viewport';
import type { AppContext } from '../app/AppContext';
import { GameName, GameState, GameWaitState, GameTime, RoomName } from '../net/enums';
import { MessageTag } from '../net/messageTags';
import { encodeGetNewGameConfigs, encodeNewGame, decodeNewGameConfigs } from '../net/codec/matchmaking';
import type { NewGameConfig } from '../net/models/newGameConfig';
import type { GameInfoModel } from '../net/models/gameInfo';
import { gameState } from '../state/GameState';
import { moneyFa, gameNameLabel } from '../util/persian';
import { formatMoneyInput } from '../util/moneyInput';
import {
  formatGamePriceK,
  normalizeGamePrice,
  parseGamePriceK,
  stepGamePrice,
} from '../util/gamePriceInput';

/**
 * Create-game form (port of NewGameUi.cs), re-skinned to the real game: username/balance info-pills +
 * avatar header, a title, and right-label/left-control rows (count/type/time dropdowns + synchronized
 * K-unit price field, step buttons, and slider) directly on the wood. Submits via NewGame(48).
 */
export class CreateGameScene extends Scene {
  private config: NewGameConfig | null = null;
  private readonly bgLayer = new Container();
  private readonly body = new Container();
  private entered = false;

  private countPicker?: OptionPicker;
  private typePicker?: OptionPicker;
  private timePicker?: OptionPicker;
  private slider?: Slider;
  private priceField?: TextField;
  private priceDecreaseButton?: Button;
  private priceIncreaseButton?: Button;
  private selectedPrice: number | null = null;

  constructor(
    private readonly ctx: AppContext,
    private readonly gameName: GameName = GameName.Blackjack,
  ) {
    super();
  }

  onEnter(): void {
    this.addChild(this.bgLayer, this.body);
    this.rebuildBg();

    const loading = makeText('در حال دریافت تنظیمات…', { size: 24, fill: COLORS.textDim, align: 'center' });
    loading.anchor.set(0.5);
    loading.position.set(DESIGN_W / 2, 400);
    this.body.addChild(loading);
    this.body.addChild(pageHeader(
      `ساخت میز ${gameNameLabel(this.gameName)}`,
      () => this.ctx.navigate(RoomName.MainMenu),
    ));

    this.track(
      this.ctx.client.on(MessageTag.GetNewGameConfigs, (r) => {
        this.config = decodeNewGameConfigs(r);
        this.buildForm();
      }),
    );
    this.ctx.client.send(encodeGetNewGameConfigs(this.gameName));
  }

  /** Re-flow on viewport-height change (Telegram fullscreen / rotation): rebuild bg + form for the new height. */
  onResize(): void {
    this.rebuildBg();
    if (this.config) {
      this.commitPriceField();
      this.buildForm();
    }
  }

  /** Rebuild the full-viewport background layer (the wood fills the live height). */
  private rebuildBg(): void {
    for (const ch of this.bgLayer.removeChildren()) ch.destroy({ children: true });
    this.bgLayer.addChild(sceneBackground());
  }

  private buildForm(): void {
    const cfg = this.config;
    if (!cfg) return;
    const selectedCount = this.countPicker?.selectedIndex ?? cfg.selectedGameCountOption;
    const selectedType = this.typePicker?.selectedIndex ?? cfg.selectedGameTypeOption;
    const selectedTime = this.timePicker?.selectedIndex ?? cfg.selectedGameTimeOption;
    // Destroy (not just detach) so a resize re-flow tears down old pickers and any of their DOM overlays.
    for (const ch of this.body.removeChildren()) ch.destroy({ children: true });
    this.countPicker = undefined;
    this.typePicker = undefined;
    this.timePicker = undefined;
    this.slider = undefined;
    this.priceField = undefined;
    this.priceDecreaseButton = undefined;
    this.priceIncreaseButton = undefined;

    // Unified header glass card (mirrors the lobby header): the game icon (left) marks which game is
    // being created, the avatar sits on the right, and the username/balance render as plain text on the
    // card — no nested info-pill frames (the double-frame lesson).
    const s = gameState.get();
    const hX = 24;
    const hY = 112;
    const hW = DESIGN_W - 48;
    const hH = 116;
    this.body.addChild(glassCard(hX, hY, hW, hH));

    const gameIco = iconSprite(this.ctx.gameIcon(this.gameName), 84);
    gameIco.position.set(hX + 22, hY + (hH - 84) / 2);
    this.body.addChild(gameIco);

    const avSize = 88;
    const avX = hX + hW - 20 - avSize;
    const avY = hY + (hH - avSize) / 2;
    const avatar = avatarSprite(this.ctx.profileAtlas, s.avatar, avSize);
    avatar.position.set(avX, avY);
    this.body.addChild(avatar);

    const rightX = avX - 18;
    const nameText = makeText(s.username || '—', { size: 26, weight: '700', fill: COLORS.text, align: 'right' });
    nameText.anchor.set(1, 0);
    nameText.position.set(rightX, hY + 26);
    this.body.addChild(nameText);

    // Balance row: «موجودی: …» with a leading coin icon (RTL).
    const coinSize = 26;
    const balanceText = makeText(`موجودی: ${moneyFa(s.balance)}`, { size: 22, weight: '700', fill: COLORS.gold, align: 'right' });
    balanceText.anchor.set(1, 0);
    balanceText.position.set(rightX - coinSize - 8, hY + 68);
    this.body.addChild(balanceText);
    const coin = iconSprite(this.ctx.iconTexture('coin'), coinSize);
    coin.position.set(rightX - coinSize, hY + 67);
    this.body.addChild(coin);

    this.body.addChild(pageHeader(
      `ساخت میز ${gameNameLabel(this.gameName)}`,
      () => this.ctx.navigate(RoomName.MainMenu),
    ));

    // iOS form pattern: every label sits above a full-width control. This reads in one RTL scan path
    // and gives every picker a 48pt-class touch target instead of the old split desktop row.
    const ctrlW = DESIGN_W - 112;
    const CTRL_H = 88;
    const ROW_PITCH = 142;
    const rowLabel = (text: string, top: number): void => {
      const label = makeText(text, { size: 28, weight: '700', align: 'right', fill: COLORS.text });
      label.anchor.set(1, 0);
      label.position.set(DESIGN_W - 56, top);
      this.body.addChild(label);
    };

    const hasType = cfg.gameTypeOptions.length > 0;
    const rowCount = hasType ? 4 : 3; // count, [type], time, price stepper + slider
    const extraShift = Math.min(72, Math.max(0, viewport.h - 1280) * 0.14);
    const startY = 268 + extraShift;
    const rowY = (i: number): number => startY + i * ROW_PITCH;

    const cardPad = 24;
    const cardX = 24;
    const cardW = DESIGN_W - 48;
    const cardTop = startY - cardPad;
    const cardH = rowCount * ROW_PITCH + cardPad + 76;
    this.body.addChild(glassCard(cardX, cardTop, cardW, cardH));

    let r = 0;

    this.countPicker = new OptionPicker({
      width: ctrlW,
      height: CTRL_H,
      options: cfg.gameCountOptions.map((o) => o.text),
      selected: selectedCount,
      // Each game-count option carries its own MinPrice; changing it re-bounds the price slider.
      onChange: () => this.applyPriceRange(),
    });
    rowLabel(cfg.gameCountTitle || 'تعداد بازی', rowY(r));
    this.countPicker.position.set(56, rowY(r) + 38);
    this.body.addChild(this.countPicker);
    r++;

    if (hasType) {
      this.typePicker = new OptionPicker({
        width: ctrlW,
        height: CTRL_H,
        options: cfg.gameTypeOptions.map((o) => o.text),
        selected: selectedType,
      });
      rowLabel(cfg.gameTypeTitle || 'نوع بازی', rowY(r));
      this.typePicker.position.set(56, rowY(r) + 38);
      this.body.addChild(this.typePicker);
      r++;
    }

    this.timePicker = new OptionPicker({
      width: ctrlW,
      height: CTRL_H,
      options: cfg.gameTimeOptions.map((o) => o.text),
      selected: selectedTime,
    });
    rowLabel(cfg.gameTimeTitle || 'زمان بازی', rowY(r));
    this.timePicker.position.set(56, rowY(r) + 38);
    this.body.addChild(this.timePicker);
    r++;

    const priceY = rowY(r);
    rowLabel('ورودی میز', priceY);
    const initialMin = this.effectiveMin();
    const initialPrice = normalizeGamePrice(
      BigInt(this.selectedPrice ?? initialMin),
      initialMin,
      cfg.gamePriceMax,
      cfg.gamePriceStep,
    );
    this.selectedPrice = initialPrice;

    const controlX = 56;
    const stepperY = priceY + 40;
    const stepButtonW = 68;
    const stepButtonH = 68;
    const stepperGap = 12;
    const suffixW = 32;
    const priceFieldW = ctrlW - stepButtonW * 2 - stepperGap * 3 - suffixW;

    this.priceDecreaseButton = new Button('−', () => this.stepPrice(-1), {
      width: stepButtonW,
      height: stepButtonH,
      fontSize: 34,
      fill: COLORS.panelSoft,
      textColor: COLORS.text,
      accessibleLabel: 'کاهش ورودی میز به اندازه یک پله',
    });
    this.priceDecreaseButton.position.set(controlX, stepperY);
    this.body.addChild(this.priceDecreaseButton);

    const priceFieldX = controlX + stepButtonW + stepperGap;
    this.priceField = new TextField({
      width: priceFieldW,
      height: stepButtonH,
      value: formatGamePriceK(initialPrice),
      placeholder: 'مبلغ',
      align: 'center',
      accessibilityLabel: 'مبلغ ورودی میز به هزار',
      inputMode: 'numeric',
      enterKeyHint: 'done',
    });
    this.priceField.position.set(priceFieldX, stepperY);
    this.priceField.onChange((text) => {
      const formatted = formatMoneyInput(text);
      if (this.priceField && this.priceField.value !== formatted) this.priceField.value = formatted;
    });
    this.priceField.onEnter(() => this.commitPriceField());
    this.body.addChild(this.priceField);

    const suffix = makeText('K', { size: 28, weight: '700', fill: COLORS.gold, align: 'center' });
    suffix.anchor.set(0.5);
    suffix.position.set(priceFieldX + priceFieldW + stepperGap + suffixW / 2, stepperY + stepButtonH / 2);
    this.body.addChild(suffix);

    this.priceIncreaseButton = new Button('+', () => this.stepPrice(1), {
      width: stepButtonW,
      height: stepButtonH,
      fontSize: 32,
      fill: COLORS.panelSoft,
      textColor: COLORS.text,
      accessibleLabel: 'افزایش ورودی میز به اندازه یک پله',
    });
    this.priceIncreaseButton.position.set(controlX + ctrlW - stepButtonW, stepperY);
    this.body.addChild(this.priceIncreaseButton);

    this.slider = new Slider({
      width: ctrlW,
      min: initialMin,
      max: cfg.gamePriceMax,
      step: cfg.gamePriceStep,
      value: initialPrice,
      onChange: (v) => this.setPrice(v, false),
    });
    this.slider.position.set(controlX, priceY + 112);
    this.body.addChild(this.slider);
    this.setPrice(initialPrice);

    const submit = new Button('ساخت میز و پیدا کردن حریف', () => this.submit(), {
      width: DESIGN_W - 80,
      height: 88,
      fontSize: 28,
      accessibleLabel: `ساخت میز ${gameNameLabel(this.gameName)} و پیدا کردن حریف`,
    });
    submit.position.set(40, viewport.h - 104);
    this.body.addChild(submit);

    if (!this.entered) {
      this.entered = true;
      enterFade([this.body]);
    }
  }

  /** Effective price floor = max(global GamePriceMin, the selected game-count option's MinPrice). */
  private effectiveMin(): number {
    const cfg = this.config;
    if (!cfg) return 0;
    const perCount = cfg.gameCountOptions[this.countPicker?.selectedIndex ?? 0]?.minPrice ?? 0;
    return Math.max(cfg.gamePriceMin, perCount);
  }

  /** Re-bound the price controls when the game-count option changes. */
  private applyPriceRange(): void {
    const cfg = this.config;
    if (!cfg || !this.slider) return;
    this.slider.setRange(this.effectiveMin(), cfg.gamePriceMax, false);
    this.setPrice(this.selectedPrice ?? this.slider.value);
  }

  /** Commit typed K units to the nearest configured price step. Empty edits restore the last value. */
  private commitPriceField(): void {
    const cfg = this.config;
    const field = this.priceField;
    if (!cfg || !field) return;
    if (!field.value) {
      this.setPrice(this.selectedPrice ?? this.effectiveMin());
      return;
    }
    const price = normalizeGamePrice(
      parseGamePriceK(field.value),
      this.effectiveMin(),
      cfg.gamePriceMax,
      cfg.gamePriceStep,
    );
    this.setPrice(price);
  }

  /** Apply an authoritative full price to the field, slider, and step-button states. */
  private setPrice(price: number, updateSlider = true): void {
    const cfg = this.config;
    if (!cfg) return;
    const normalized = normalizeGamePrice(
      BigInt(Math.trunc(price)),
      this.effectiveMin(),
      cfg.gamePriceMax,
      cfg.gamePriceStep,
    );
    this.selectedPrice = normalized;
    if (updateSlider) this.slider?.setValue(normalized, false);
    if (this.priceField) this.priceField.value = formatGamePriceK(normalized);
    this.priceDecreaseButton?.setEnabled(normalized > this.effectiveMin());
    this.priceIncreaseButton?.setEnabled(normalized < cfg.gamePriceMax);
  }

  /** Move exactly one server-configured price increment. */
  private stepPrice(direction: -1 | 1): void {
    const cfg = this.config;
    if (!cfg) return;
    const price = stepGamePrice(
      this.selectedPrice ?? this.effectiveMin(),
      direction,
      this.effectiveMin(),
      cfg.gamePriceMax,
      cfg.gamePriceStep,
    );
    this.setPrice(price);
  }

  private submit(): void {
    const cfg = this.config;
    if (!cfg || !this.countPicker || !this.timePicker || !this.slider) return;

    this.commitPriceField();

    const count = cfg.gameCountOptions[this.countPicker.selectedIndex]?.value ?? 0;
    const type = cfg.gameTypeOptions[this.typePicker?.selectedIndex ?? 0]?.value ?? 0;
    const time = cfg.gameTimeOptions[this.timePicker.selectedIndex]?.value ?? GameTime.Normal;

    const price = this.selectedPrice ?? this.slider.value;

    const info: GameInfoModel = {
      uniqueId: '',
      gameName: this.gameName,
      gameState: GameState.Wait,
      gameTime: time as GameTime,
      gameWaitState: GameWaitState.Created,
      gamePrice: BigInt(price),
      gameCount: count,
      gameType: type,
    };
    this.ctx.client.send(encodeNewGame(info));
    // Server replies with a fresh MainMenu(24); return to the lobby (now showing "waiting").
    this.ctx.navigate(RoomName.MainMenu);
  }
}
